import { formatCouponRateWithNotation } from "@/components/widgets/prices/helper/cellRenderers/CouponRateCellRenderer/couponRateFormatter";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import { formatNumberWithCommas } from "@/lib/utilities";
import {
  returnIndicativePrice,
  returnIndicativePrincipal,
} from "../helper/bondTicketHelper";
import {
  ITicketFieldsProps,
  TICKET_EMPTY_VALUE,
} from "../helper/ticketSharedHelper";
import TicketRow from "../ticketFields/TicketRow";
import { useBondTicket } from "./useBondTicket";

// Everything past the core fields: read-only context for the chosen bond. Becomes the
// Trade Creation Security Details panel content.

function returnPriceDisplay(price: number | null): string {
  return price === null ? TICKET_EMPTY_VALUE : String(price);
}

function returnPrincipalDisplay(principal: number | null): string {
  if (principal === null) return TICKET_EMPTY_VALUE;
  return formatNumberWithCommas(Number(principal.toFixed(2)));
}

const BondDetailFields = ({ ticketId, showSecurityIdentity }: ITicketFieldsProps) => {
  const { ticket, security } = useBondTicket(ticketId);
  const price = returnIndicativePrice(security);
  const principal = returnIndicativePrincipal(ticket.nominal, price);

  return (
    <div
      data-testid="BondDetailFieldsContainer"
      id="BondDetailFieldsContainer"
      className="flex flex-col gap-2"
    >
      {showSecurityIdentity && (
        <>
          <TicketRow label="ISIN" testId="BondDetailIsinRow">
            <div className="text-xs dark:text-gray-200">
              {ticket.isin || TICKET_EMPTY_VALUE}
            </div>
          </TicketRow>
          <TicketRow label="Security Name" testId="BondDetailSecurityNameRow">
            <div className="text-xs dark:text-gray-200">
              {ticket.securityName || TICKET_EMPTY_VALUE}
            </div>
          </TicketRow>
        </>
      )}

      {/* LQC-2951 - spec F6 lists Coupon Rate; GET /Securities/bonds carries it. */}
      <TicketRow label="Coupon" testId="BondTicketCouponRow">
        <div className="text-xs dark:text-gray-200">
          {formatCouponRateWithNotation(security?.couponRate) || TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>
      <TicketRow label="Trade Date" testId="BondTicketTradeDateRow">
        <div className="text-xs dark:text-gray-200">
          {ticket.tradeDate
            ? returnFormattedDate(ticket.tradeDate)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Currency" testId="BondTicketCurrencyRow">
        <div className="text-xs dark:text-gray-200">
          {security?.currency || TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Maturity" testId="BondTicketMaturityRow">
        <div className="text-xs dark:text-gray-200">
          {security?.maturityDate
            ? returnFormattedDate(security.maturityDate)
            : TICKET_EMPTY_VALUE}
        </div>
      </TicketRow>

      <TicketRow label="Price (mid)" testId="BondTicketPriceRow">
        <div className="text-xs dark:text-gray-200">
          {returnPriceDisplay(price)}
        </div>
      </TicketRow>

      <TicketRow label="Principal" testId="BondTicketPrincipalRow">
        <div className="text-xs dark:text-gray-200">
          {returnPrincipalDisplay(principal)}
        </div>
      </TicketRow>
    </div>
  );
};

export default BondDetailFields;
